/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/LanguageSelector/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { use, useState, useEffect, useMemo } from "react";
import Select, { components } from "react-select";
import { KeyboardArrowDown } from "@mui/icons-material";
import styles from "./LanguageSelector.module.css";
import { useTranslation } from "react-i18next";
import { getLanguageOptions } from "../../data/selectOptions";
import { AppContext } from "../../context/AppContext";

const CONTAINER_MARGIN_LEFT = "0px";
const DROPDOWN_INDICATOR_PADDING_LEFT = "0px";

const DropdownIndicator = (props) => {
  const { selectProps } = props;

  return (
    <components.DropdownIndicator {...props}>
      <KeyboardArrowDown
        style={{
          transform: selectProps.menuIsOpen ? "rotate(180deg)" : "rotate(0deg)",
          transition: "transform 0.3s ease-in-out",
          color: "#3B3B3D",
        }}
      />
    </components.DropdownIndicator>
  );
};

const LanguageSelector = () => {
  const { language, changeLanguage } = use(AppContext);
  const { t, i18n } = useTranslation();
  const languageOptions = useMemo(
    () => getLanguageOptions(t),
    [t, i18n.language],
  );
  const [isTablet, setIsTablet] = useState(false);
  const [isMediumMobile, setIsMediumMobile] = useState(false);
  const [isSmallMobile, setIsSmallMobile] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsTablet(window.innerWidth <= 1023 && window.innerWidth >= 768);
      setIsMediumMobile(window.innerWidth <= 767 && window.innerWidth >= 481);
      setIsSmallMobile(window.innerWidth <= 480);
      setIsMobile(window.innerWidth <= 767);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  const formatOptionLabel = (option, { context }) => {
    const showOnlyFlag = (isMobile || isTablet) && context === "value";

    return (
      <div className={styles.language}>
        <img
          className={styles.language_img}
          src={option.image}
          alt={t("common.languageFlagAlt")}
        />
        {!showOnlyFlag && (
          <span className={styles.language_name}>{option.label}</span>
        )}
      </div>
    );
  };

  const getContainerWidth = () => {
    if (isSmallMobile) {
      return "90px";
    }
    if (isMediumMobile) {
      return "150px";
    }
    if (isTablet) {
      return "90px";
    }
    return "192px";
  };

  const getContainerPaddingLeft = () => {
    if (isSmallMobile) {
      return "8px";
    }
    if (isMediumMobile) {
      return "0px";
    }
    if (isTablet) {
      return "0px";
    }
    return "0px";
  };

  const getContainerPaddingRight = () => {
    if (isSmallMobile) {
      return "4px";
    }
    if (isMediumMobile) {
      return "0px";
    }
    if (isTablet) {
      return "0px";
    }
    return "0px";
  };

  const getMenuWidth = () => {
    if (isSmallMobile) {
      return "150px";
    }
    if (isMediumMobile) {
      return "192px";
    }
    if (isTablet) {
      return "192px";
    }
    return "192px";
  };

  const getMenuMinWidth = () => {
    if (isSmallMobile) {
      return "150px";
    }
    if (isMediumMobile) {
      return "192px";
    }
    if (isTablet) {
      return "192px";
    }
    return "192px";
  };

  const getMenuRight = () => {
    if (isSmallMobile) {
      return "0";
    }
    if (isMediumMobile) {
      return "0";
    }
    if (isTablet) {
      return "0";
    }
    return "auto";
  };

  return (
    <Select
      components={{ DropdownIndicator }}
      menuPosition="fixed"
      menuPortalTarget={document.body}
      styles={{
        control: (baseStyles) => ({
          ...baseStyles,
          borderRadius: "8px",
          width: "100%",
          height: "40px",
          fontSize: "16px",
          cursor: "pointer",
          border: "1px solid #909092",
          backgroundColor: "#fcfcfc",
        }),
        container: (baseStyles) => ({
          ...baseStyles,
          width: getContainerWidth(),
          paddingRight: getContainerPaddingRight(),
          paddingLeft: getContainerPaddingLeft(),
          marginLeft: CONTAINER_MARGIN_LEFT,
        }),
        valueContainer: (baseStyles) => ({
          ...baseStyles,
        }),
        input: (baseStyles) => ({
          ...baseStyles,
        }),
        option: (baseStyles) => ({
          ...baseStyles,
          cursor: "pointer",
        }),
        indicatorSeparator: () => ({ display: "none" }),
        dropdownIndicator: (baseStyles) => ({
          ...baseStyles,
          paddingLeft: DROPDOWN_INDICATOR_PADDING_LEFT,
        }),
        menu: (baseStyles) => ({
          ...baseStyles,
          zIndex: 9999,
          width: getMenuWidth(),
          minWidth: getMenuMinWidth(),
          right: getMenuRight(),
        }),
        menuPortal: (baseStyles) => ({
          ...baseStyles,
          zIndex: 9999,
        }),
      }}
      isSearchable={false}
      value={language}
      onChange={changeLanguage}
      options={languageOptions}
      formatOptionLabel={formatOptionLabel}
    />
  );
};

export default LanguageSelector;
